import { FocusEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FormikErrors } from 'formik';
import { useSelector } from "react-redux";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { getActiveLanguage, getLanguageContent } from "@redux/selectors/language-selectors";
import { Logger } from "@utils/logger";
import { formatWithLocale, getDateFormatForLocale, parseDateForLocale } from "@iddataweb/idw-common-ui/utils";
import { AXNUIDateInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';

type DateOfBirthInputProps = {
    title: string;
    attribute: AttributeFormState;
    fieldName: string;
    fieldTouched: boolean;
    fieldError: string;
    fieldIsInvalid: boolean;
    fieldShouldBeHighlighted: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
};

export const DateOfBirthInput = (props: DateOfBirthInputProps) => {
    const {
        title,
        attribute,
        fieldName,
        fieldTouched,
        fieldError,
        fieldIsInvalid,
        fieldShouldBeHighlighted,
        setFieldValue,
        setFieldTouched,
        onBlur,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const content = useSelector(getLanguageContent);
    const activeLanguage = useSelector(getActiveLanguage);
    const locale = useMemo(() => activeLanguage, [activeLanguage]);
    const dateFormat = useMemo(() => getDateFormatForLocale(locale), [locale]);
    const inputRef = useRef<HTMLInputElement>(null);
    const [typedDate, setTypedDate] = useState<string>("");

    const initializeWithInputData = async () => {
        const dateString = `${attribute.inputData.month}/${attribute.inputData.day}/${attribute.inputData.year}`;
        const date = parseDateForLocale(dateString, LocaleTypes.ENGLISH);
        if (date) {
            const formattedDate = formatWithLocale(date, dateFormat, locale);
            setTypedDate(formattedDate);
            await setFieldValue(fieldName, formattedDate);
        }
    };

    useEffect(() => {
        if (attribute.inputData.month && attribute.inputData.day && attribute.inputData.year) {
            initializeWithInputData();
        }
    }, [locale]);

    const findNonMatchingCharacters = (input: string, matchChars: string) => {
        const matchSet = new Set(matchChars.toLowerCase());
        const seen = new Set<string>();
        const result: Array<string> = [];

        for (const char of input) {
            const lowerChar = char.toLowerCase();
            if (!matchSet.has(lowerChar) && !seen.has(lowerChar)) {
                seen.add(lowerChar);
                result.push(lowerChar);
            }
        }
        return result;
    };

    const handleRawChange = async (dateString: string) => {
        Logger.info("[DateOfBirthInput] submit value: ", dateString);
        const inputData = {
            year: "",
            month: "",
            day: ""
        };

        const separators = findNonMatchingCharacters(dateFormat, "dmy");
        let separator = "/";
        if (separators.length > 0) {
            separator = separators[0];
        }
        let segments = dateFormat.split(separator);
        if (segments.length !== 3) {
            segments = ["DD", "MM", "YYYY"];
        }

        if (dateString.length === 10) {
            const date = parseDateForLocale(dateString, locale);
            inputData.year = `${date.getFullYear()}`;
            inputData.month = `${date.getMonth() + 1}`;
            inputData.day = `${date.getDate()}`;
            setTypedDate(dateString);
            await setFieldValue(fieldName, dateString);
            //@ts-ignore
            inputRef?.current?.setOpen(false);
        } else if (
            segments[0].length === 2 &&
            segments[1].length === 2 &&
            ((typedDate.length === 1 && dateString.length === 2) ||
                (typedDate.length === 4 && dateString.length === 5))
        ) {
            setTypedDate(dateString + separator);
            await setFieldValue(fieldName, dateString + separator);
        } else if (
            segments[0].length === 4 &&
            segments[1].length === 2 &&
            ((typedDate.length === 3 && dateString.length === 4) ||
                (typedDate.length === 6 && dateString.length === 7))
        ) {
            setTypedDate(dateString + separator);
            await setFieldValue(fieldName, dateString + separator);
        } else if (
            segments[0].length === 2 &&
            segments[1].length === 4 &&
            ((typedDate.length === 1 && dateString.length === 2) ||
                (typedDate.length === 6 && dateString.length === 7))
        ) {
            setTypedDate(dateString + separator);
            await setFieldValue(fieldName, dateString + separator);
        } else {
            setTypedDate(dateString);
            await setFieldValue(fieldName, dateString);
        }
        onSetSubmitValue({ inputData });
        setFieldTouched(fieldName, true, true);
    };

    const handleChange = async (date: Date) => {
        Logger.info('[DateOfBirthInput] submit value: ', date);
        if (date) {
            const formattedDate = formatWithLocale(date, dateFormat, locale);
            const inputData = {
                year: `${date.getFullYear()}`,
                month: `${date.getMonth() + 1}`,
                day: `${date.getDate()}`
            };
            setTypedDate(formattedDate);
            onSetSubmitValue({ inputData });
            await setFieldValue(fieldName, formattedDate);
            //@ts-ignore
            inputRef?.current?.setOpen(false);
        } else {
            const inputData = { year: "", month: "", day: "" };
            setTypedDate("");
            onSetSubmitValue({ inputData });
            await setFieldValue(fieldName, "");
        }
        setFieldTouched(fieldName, true, true);
    };

    return (
        <AXNUIDateInputWithErrorMessage
            autocomplete={"new-password"}
            controlId={`attr${attribute.id}-date-input`}
            automationId={`attr${attribute.id}-date-input`}
            onChange={handleChange}
            onRawChange={handleRawChange}
            inputRef={inputRef}
            fieldName={fieldName}
            fieldTouched={fieldTouched}
            fieldError={fieldError}
            fieldIsInvalid={fieldIsInvalid}
            fieldShouldBeHighlighted={fieldShouldBeHighlighted}
            onBlur={onBlur}
            setFieldTouched={setFieldTouched}
            typedDate={typedDate}
            isDisabled={attribute.isDisabled}
            isPlaceholder={attribute.isPlaceholder}
            isOptional={attribute.optional}
            isSubmitting={isSubmitting}
            title={title}
            placeholder={attribute.placeholder
                ? attribute.placeholder
                : content.form.date.dateFormat}
            showYearDropdown={true}
            showMonthDropdown={true}
            dropdownMode={"select"}
            dateFormat={dateFormat}
            locale={locale}
        />
    );
};